import {
  FALL_DURATION,
  FRAME_RATE,
  WALK_SPEED,
  type GameState,
  type Mine,
  type Tank,
  type TurnEvents,
  type ProjectileKind,
} from '@tanky/shared';
import type { Effects } from './effects';
import type { TerrainGfx } from './terrainGfx';
import { sound } from '../sound';

export type DisplayTank = Pick<Tank, 'id' | 'name' | 'color' | 'x' | 'y' | 'hp' | 'alive' | 'angle' | 'power' | 'look' | 'target'> & {
  /** Zpětný ráz po výstřelu 0–1 (jen vizuální). */
  recoil?: number;
};

/** Síla zpětného rázu podle zbraně (0–1). */
const RECOIL: Partial<Record<string, number>> = {
  nuke: 1,
  heavy: 0.9,
  shotgun: 0.85,
  bunker: 0.8,
  blackhole: 0.7,
  laser: 0.25,
  sheep: 0.3,
  teleport: 0.3,
  mine: 0.3,
};

export interface ProjectileView {
  x: number;
  y: number;
  weapon: ProjectileKind;
  /** Čas letu projektilu (s) – pro rotaci banánu/válce apod. */
  age: number;
  vx: number;
  vy: number;
  bomblet: boolean;
  trail: number[];
  /** Hloubka ve 3D (kamikadze dron). */
  z?: number;
  /** Natočení ve 3D (kamikadze dron). */
  yaw?: number;
}

function byTime<T extends { t: number }>(list: T[]): T[] {
  return list.map((e, i) => ({ e, i })).sort((a, b) => a.e.t - b.e.t || a.i - b.i).map((x) => x.e);
}

export function cloneTanks(tanks: Tank[]): DisplayTank[] {
  return tanks.map((t) => ({ id: t.id, name: t.name, color: t.color, x: t.x, y: t.y, hp: t.hp, alive: t.alive, angle: t.angle, power: t.power, look: t.look, target: t.target }));
}

/**
 * Přehrává výsledek tahu spočítaný serverem. Terén upravuje postupně podle časů výbuchů.
 */
export class Playback {
  t = 0;
  readonly tanks: DisplayTank[];
  readonly shooter: DisplayTank | undefined;
  private ops;
  private explosions;
  private damage;
  private deaths;
  private splashes;
  /** Projektily seřazené podle startu – kvůli zvukům výstřelu. */
  private launches: { t0: number; weapon: string; x: number; main: boolean }[];
  private launchCursor = 0;
  private burning = false;
  private teleports;
  /** Miny zobrazené během přehrávání (stav před tahem + položené/zničené). */
  readonly mines: Mine[];
  private mineEvents;
  private pulls;
  private holes;
  private bolts;
  private quakes;
  private cursor2 = { mines: 0, holes: 0, bolts: 0, quakes: 0 };
  private walkDone = false;
  private cursor = { ops: 0, explosions: 0, damage: 0, deaths: 0, splashes: 0, teleports: 0 };
  /** Pořadí opů se podle času nezměnilo → terén bude po přehrání přesně jako na serveru. */
  readonly opsInOrder: boolean;
  private fallStart = new Map<string, { t: number; from: number; to: number }[]>();

  constructor(
    readonly events: TurnEvents,
    before: GameState,
    private gfx: TerrainGfx,
    private fx: Effects,
    private dirtColor: string,
  ) {
    this.tanks = cloneTanks(before.tanks);
    this.shooter = this.tanks.find((t) => t.id === events.playerId);
    this.recoilAt = events.projectiles[0]?.t0 ?? -1;
    this.recoilPower = RECOIL[events.input?.weapon ?? ''] ?? 0.6;
    if (this.shooter && events.input) {
      this.shooter.angle = events.input.angle;
      this.shooter.power = events.input.power;
    }
    this.ops = byTime(events.ops);
    this.opsInOrder = this.ops.every((o, i) => o === events.ops[i]);
    this.explosions = byTime(events.explosions);
    this.damage = byTime(events.damage);
    this.deaths = byTime(events.deaths);
    this.splashes = byTime(events.splashes);
    this.launches = events.projectiles
      .map((p, i) => ({ t0: p.t0, weapon: p.weapon, x: p.pts[0], main: i === 0 }))
      .sort((a, b) => a.t0 - b.t0);
    this.teleports = byTime(events.teleports ?? []);
    this.mines = (before.mines ?? []).map((m) => ({ ...m }));
    this.mineEvents = byTime(events.mines ?? []);
    this.pulls = events.pulls ?? [];
    this.holes = byTime(events.blackholes ?? []);
    this.bolts = byTime(events.lightnings ?? []);
    this.quakes = byTime(events.quakes ?? []);
    for (const f of events.falls) {
      const list = this.fallStart.get(f.id) ?? [];
      list.push({ t: f.t, from: f.fromY, to: f.toY });
      this.fallStart.set(f.id, list);
    }
  }

  /** Čas a síla výstřelu střelce – pro zpětný ráz. */
  private recoilAt = -1;
  private recoilPower = 0.6;

  get done() {
    return this.t >= this.events.duration;
  }

  isFalling(id: string): boolean {
    const falls = this.fallStart.get(id);
    if (!falls) return false;
    return falls.some((f) => this.t >= f.t && this.t < f.t + FALL_DURATION);
  }

  isPulled(id: string): boolean {
    return this.pulls.some((p) => p.id === id && this.t >= p.t0 && this.t <= p.t1);
  }

  /** Posune čas a spustí všechny události, které mezitím nastaly. */
  update(dt: number) {
    this.t = Math.min(this.events.duration, this.t + dt);
    const t = this.t;
    if (this.shooter && this.recoilAt >= 0) {
      // Rychlé cuknutí a pak doznívání (~0,6 s)
      const k = t - this.recoilAt;
      this.shooter.recoil = k < 0 || k > 0.7 ? 0 : this.recoilPower * Math.exp(-k * 7) * Math.min(1, k * 40);
    }
    const c = this.cursor;

    // Zvuky startů projektilů
    while (this.launchCursor < this.launches.length && this.launches[this.launchCursor].t0 <= t) {
      const l = this.launches[this.launchCursor++];
      if (l.main) sound.fire(l.weapon as never, l.x);
      else if (l.weapon === 'airbomb' || l.weapon === 'bomber') sound.whistle(l.x);
      else if (l.weapon === 'flame' && !this.burning) {
        this.burning = true;
        sound.burn(l.x, 3.2);
      }
    }

    const walk = this.events.walk;
    // Jen během chůze (a jednou na jejím konci) – potom může tankem hnout teleport
    if (walk && this.shooter && walk.path.length && !this.walkDone) {
      if (t >= walk.duration) this.walkDone = true;
      const n = walk.path.length / 2;
      const i = Math.min(n - 1, Math.floor(t * WALK_SPEED));
      this.shooter.x = walk.path[i * 2];
      this.shooter.y = walk.path[i * 2 + 1];
    }

    while (c.ops < this.ops.length && this.ops[c.ops].t <= t) {
      const slide = this.gfx.applyOp(this.ops[c.ops++].op);
      if (slide) {
        this.fx.slide(slide.moves, slide.colors, () => this.gfx.reveal(slide));
        sound.slide(slide.moves.length / 4, slide.moves[0]);
      }
    }
    while (c.explosions < this.explosions.length && this.explosions[c.explosions].t <= t) {
      const e = this.explosions[c.explosions++];
      if (e.marker) {
        this.fx.marker(e.x, e.y);
        sound.marker(e.x);
        continue;
      }
      this.fx.explosion(e.x, e.y, e.r, e.dirt, this.dirtColor, e.z ?? 0);
      if (e.dirt) sound.dirt(e.x);
      else sound.explosion(e.r, e.x, e.y);
    }
    while (c.splashes < this.splashes.length && this.splashes[c.splashes].t <= t) {
      const x = this.splashes[c.splashes++].x;
      this.fx.splash(x);
      sound.splash(x);
    }
    while (c.teleports < this.teleports.length && this.teleports[c.teleports].t <= t) {
      const tp = this.teleports[c.teleports++];
      const tank = this.tanks.find((x) => x.id === tp.id);
      this.fx.teleport(tp.fromX, tp.fromY - 6);
      sound.teleport(tp.toX);
      this.fx.teleport(tp.toX, tp.toY - 6);
      if (tank) {
        tank.x = tp.toX;
        tank.y = tp.toY;
      }
    }
    while (c.damage < this.damage.length && this.damage[c.damage].t <= t) {
      const d = this.damage[c.damage++];
      const tank = this.tanks.find((x) => x.id === d.id);
      if (tank) {
        tank.hp = Math.max(0, tank.hp - d.amount);
        this.fx.damage(tank.x, tank.y - 28, d.amount);
        sound.hit(tank.x);
      }
    }
    for (const tank of this.tanks) {
      // Přitažení černou dírou
      for (const p of this.pulls) {
        if (p.id !== tank.id || t < p.t0) continue;
        const k = Math.min(1, (t - p.t0) / Math.max(0.01, p.t1 - p.t0));
        const e = k * k * (3 - 2 * k);
        tank.x = p.fromX + (p.toX - p.fromX) * e;
        tank.y = p.fromY + (p.toY - p.fromY) * e;
      }
      const falls = this.fallStart.get(tank.id);
      const f = falls?.filter((x) => x.t <= t).at(-1);
      if (!f) continue;
      const k = Math.min(1, (t - f.t) / FALL_DURATION);
      tank.y = f.from + (f.to - f.from) * k * k;
    }
    const c2 = this.cursor2;
    while (c2.mines < this.mineEvents.length && this.mineEvents[c2.mines].t <= t) {
      const m = this.mineEvents[c2.mines++];
      if (m.kind === 'placed') {
        this.mines.push({ id: m.id, x: m.x, y: m.y, owner: this.events.playerId, armedTurn: Infinity });
        sound.mineBeep(m.x);
      } else {
        const i = this.mines.findIndex((x) => x.id === m.id);
        if (i >= 0) this.mines.splice(i, 1);
      }
    }
    while (c2.holes < this.holes.length && this.holes[c2.holes].t <= t) {
      const h = this.holes[c2.holes++];
      this.fx.blackhole(h.x, h.y, h.dur);
      sound.blackhole(h.x);
    }
    while (c2.bolts < this.bolts.length && this.bolts[c2.bolts].t <= t) {
      const b = this.bolts[c2.bolts++];
      this.fx.lightning(b.x, b.y0, b.y1);
      sound.thunder(b.x);
    }
    while (c2.quakes < this.quakes.length && this.quakes[c2.quakes].t <= t) {
      const q = this.quakes[c2.quakes++];
      this.fx.quake(q.dur);
      sound.quake();
    }
    while (c.deaths < this.deaths.length && this.deaths[c.deaths].t <= t) {
      const d = this.deaths[c.deaths++];
      const tank = this.tanks.find((x) => x.id === d.id);
      if (!tank) continue;
      tank.alive = false;
      if (d.cause !== 'water') sound.death(tank.x);
      if (d.cause === 'water') {
        this.fx.splash(tank.x);
      } else {
        this.fx.explosion(tank.x, tank.y - 6, 26, false, this.dirtColor);
      }
      const label = d.cause === 'timeout' ? '⌛ Vypršel čas' : d.cause === 'surrender' ? '🏳 Vzdal se' : d.cause === 'water' ? '🌊 Utopen' : '💀 Zničen';
      this.fx.label(tank.x, tank.y - 40, label);
    }
  }

  /**
   * Přeskočí let řízené střely / kamikadze dronu / bombardéru – hráč
   * ho už viděl živě, takže se hned ukáže výbuch a výsledek akce bez opakování letu. Vrací, zda se něco přeskočilo.
   */
  skipFlight(): boolean {
    const w = this.events.input?.weapon;
    const main = this.events.projectiles[0];
    if (!this.events.input?.steer || (w !== 'guided' && w !== 'kamikaze' && w !== 'bomber') || !main) return false;

    if (w === 'bomber') {
      while (this.launchCursor < this.launches.length) this.launchCursor++;
      this.recoilAt = -1;
      this.finish();
      return true;
    }

    const end = main.t0 + (main.pts.length / 2 - 1) / FRAME_RATE - 0.001;
    if (end <= this.t) return false;
    // Výstřel už zazněl při živém letu
    while (this.launchCursor < this.launches.length && this.launches[this.launchCursor].t0 <= end) this.launchCursor++;
    this.recoilAt = -1;
    this.update(end - this.t);
    return true;
  }

  /** Dokončí přehrávání okamžitě (přeskočení). */
  finish() {
    this.update(this.events.duration + 1);
  }

  projectiles(): ProjectileView[] {
    const out: ProjectileView[] = [];
    for (const p of this.events.projectiles) {
      const n = p.pts.length / 2;
      const f = (this.t - p.t0) * FRAME_RATE;
      if (f < 0 || f > n - 1) continue;
      const i = Math.floor(f);
      const k = f - i;
      const j = Math.min(n - 1, i + 1);
      const x = p.pts[i * 2] + (p.pts[j * 2] - p.pts[i * 2]) * k;
      const y = p.pts[i * 2 + 1] + (p.pts[j * 2 + 1] - p.pts[i * 2 + 1]) * k;
      // Laser kreslíme po celé dráze, ostatní jen krátkou stopu
      const from = p.weapon === 'laser' ? 0 : Math.max(0, i - 14);
      const vx = (p.pts[j * 2] - p.pts[i * 2]) * FRAME_RATE;
      const vy = (p.pts[j * 2 + 1] - p.pts[i * 2 + 1]) * FRAME_RATE;
      const z = p.z ? p.z[i] + ((p.z[j] ?? p.z[i]) - p.z[i]) * k : undefined;
      // Natočení podle pohybu v rovině x–z (při visení se nemění → undefined)
      const dz = p.z ? (p.z[j] ?? p.z[i]) - p.z[i] : 0;
      const dx = p.pts[j * 2] - p.pts[i * 2];
      const yaw = p.z && Math.abs(dx) + Math.abs(dz) > 0.2 ? Math.atan2(-dz, dx) : undefined;
      out.push({ x, y, weapon: p.weapon, bomblet: !!p.bomblet, trail: p.pts.slice(from * 2, (i + 1) * 2), age: this.t - p.t0, vx, vy, ...(p.z ? { z, yaw } : {}) });
    }
    return out;
  }

  /** Kam má mířit kamera. */
  focus(): { x: number; y: number } | null {
    const ps = this.projectiles();
    if (ps.length) {
      let x = 0;
      let y = 0;
      for (const p of ps) {
        x += p.x;
        y += p.y;
      }
      return { x: x / ps.length, y: y / ps.length };
    }
    if (this.shooter && this.events.walk && this.t < this.events.walk.duration) return { x: this.shooter.x, y: this.shooter.y };
    return null;
  }
}
